/**
 * PostHog initialization singleton for the buyer-facing bidding app.
 * Centralizes the init config and provides an idempotent initPostHog() function.
 *
 * Importing this module from any client component triggers module-level initialization,
 * which runs before React effects. This ensures PostHog is ready when child component
 * effects fire (child effects run before parent effects in React).
 */
import posthog from 'posthog-js'

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || ''
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

// Module-level flag — avoids relying on undocumented posthog.__loaded internal property
let initialized = false

/**
 * Initializes PostHog. Safe to call multiple times — only initializes once.
 * Guards against SSR (typeof window) and strict-mode double-mount.
 *
 * @returns true if initialization happened on this call, false if already initialized or not applicable
 */
export function initPostHog(): boolean {
  if (initialized || !POSTHOG_KEY || typeof window === 'undefined') return false
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Disable auto-pageview — fired manually in PostHogUserIdentify on route changes
    capture_pageview: false,
    capture_pageleave: true,
    // Disable autocapture to avoid inadvertently recording buyer PII from form fields.
    // Note: explicit PII (email, name) is still sent via posthog.identify() below —
    // this is standard analytics practice and is covered by Auctogon's DPA with PostHog.
    autocapture: false,
  })
  initialized = true
  return true
}

/**
 * Returns whether PostHog has been initialized and is ready to capture events.
 */
export function isPostHogInitialized(): boolean {
  return initialized
}

// Eagerly initialize when this module is first imported (client-only; typeof window guard above).
// This runs synchronously at module-evaluation time — before any React effects fire — so all
// effects in PostHogUserIdentify (pageview, identify, group) see PostHog as ready on first render.
initPostHog()
